/**
 * HR help text for the Workflow module.
 *
 * These screens decide who can see whose records, who may act on a pending
 * approval, and when employees are allowed to raise requests at all. They are
 * access-control settings, so a wrong entry either exposes data or quietly
 * blocks people from working.
 */

const delegation = {
  delegator_employment_id: "The approver who is handing over their approvals, usually because they are away.",
  delegator_name: "The approver whose approvals are being handed over.",
  delegatee_employment_id: "The colleague who will act on those approvals. While this delegation is active they can approve on the delegator's behalf, and the history records that it was delegated.",
  delegatee_name: "The colleague acting on the delegator's behalf.",
  delegation_types: "Which kinds of request are handed over, such as leave or attendance. Anything not selected still waits for the original approver.",
  remarks: "Reason for the delegation, such as annual leave. Kept for audit.",
  is_active: "Only an active delegation lets the delegatee act. Turning it off returns the approvals to the original approver immediately.",
};

const privilege = {
  employment_id: "The employee being given extra visibility. Without a privilege they see only their own records, their direct reports, or their department, depending on their employment settings.",
  all_privileges: "Give this employee visibility of every employee in the company. Turn it off to limit them with the criteria below.",
  type: "Choose whether this criterion targets named employees or a part of the organization such as departments and branches.",
  department_ids: "Employees of these departments become visible.",
  location_ids: "Employees at these locations become visible.",
  area_ids: "Employees of branches in these areas become visible.",
  branch_ids: "Employees of these branches become visible.",
  employment_ids: "These specific employees become visible.",
  criteria: "Each criterion widens what this employee can see, and they add up rather than narrowing each other. Add a second criterion to cover another group.",
};

const restrictRequest = {
  start_date: "First day of the blocked period. An employee cannot submit the selected request types for dates inside this window.",
  end_date: "Last day of the blocked period.",
  request_id: "Which request types are blocked, such as leave or attendance corrections. Employees trying to apply for dates in this window are stopped with a message naming the period.",
  description: "Why requests are blocked, such as a payroll freeze or a year-end close. Kept for audit.",
  is_active: "Only an active restriction blocks requests. Turning it off lets employees apply for that period again.",
};

const customDashboard = {
  for_employment: "Build this dashboard for one named employee. Turn it off to give it to everyone holding a role instead.",
  employment_id: "The employee who will see this dashboard when they log in.",
  role_id: "The role this dashboard is given to. Everyone holding that role sees it.",
  type: "Which dashboard layout is used. It decides the set of widgets available.",
};

const ipRestriction = {
  name: "Give this rule a clear name HR can recognize, such as the office or branch name.",
  all_company: "Turn this on when the same allowed office network should apply to every employee.",
  ip_type: "Choose how the allowed office network will be defined. IT can confirm whether to use Netmask or Range.",
  ip_address: "The office internet address employees must use to mark attendance. Ask IT for this value.",
  prefix: "An IT setting that decides how many related office addresses are included. Keep the value provided by IT.",
  mask: "This is calculated from the IP setup and helps identify the allowed office network.",
  first: "The first allowed address in this office network range.",
  last: "The last allowed address in this office network range.",
  hosts: "The total number of addresses included in this allowed office network.",
  criteria: "Use criteria when this rule should apply only to selected employees, departments, branches, areas, or locations.",
};

export const workflowTooltips = {
  delegation,
  privilege,
  restrictRequest,
  customDashboard,
  ipRestriction,
};

export default workflowTooltips;
